import React, { useCallback, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import SearchIcon from '@material-ui/icons/Search';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import SwapHorizOutlinedIcon from '@material-ui/icons/SwapHorizOutlined';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import RestoreIcon from '@material-ui/icons/Restore';

import SimpleDialog from '../Dialog/Dialog';
import { Text } from '../Inputs/Text/Text';
import { ButtonPrimary, ButtonDefault } from '../Button/Button';
import { ToastCF } from '../Toast/Toast';

import ProvaService from '../../services/Prova';
import { descreverErro } from '../../services/API';
import { cpfMask, removeCpfMask } from '../../helper/MaskHelper';

const CAMPOS_BUSCA = [
    { value: 'codigo', label: 'Código da Prova' },
    { value: 'cpf', label: 'CPF da Prova' },
];

const ROTULO_CAMPO = {
    codigo: 'Código da Prova',
    cpf: 'CPF da Prova',
};

const TAMANHO_PAGINA = 10;

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_PASSO = 0.5;

const useStyles = makeStyles(theme => ({
    contexto: {
        display: 'block',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: 4,
        border: '1px solid #d6e0ff',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        backgroundColor: '#f2f6ff',
        fontWeight: 600,
        fontSize: 14,
    },
    contextoAdendo: {
        display: 'block',
        marginTop: theme.spacing(1),
        fontWeight: 500,
    },
    slot: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: theme.spacing(2),
        border: '1px solid #e0e0e0',
        borderRadius: 4,
    },
    slotTitulo: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
    },
    radiosInline: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    radiosInlineLabel: {
        marginRight: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    buscaLinha: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    buscaCampo: {
        flex: 1,
    },
    resultados: {
        marginTop: theme.spacing(1),
        maxHeight: 220,
        overflowY: 'auto',
        border: '1px solid #eeeeee',
        borderRadius: 4,
    },
    resultado: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1),
        borderBottom: '1px solid #f2f2f2',
    },
    resultadoInfo: {
        minWidth: 0,
    },
    resultadoSecundario: {
        fontSize: 11,
        color: '#999',
    },
    vazio: {
        padding: theme.spacing(2),
        textAlign: 'center',
    },
    cartao: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        border: '1px solid #eeeeee',
        borderRadius: 4,
        backgroundColor: '#fafafa',
        textAlign: 'center',
    },
    cartaoViewport: {
        display: 'flex',
        height: 320,
        overflow: 'auto',
        border: '1px solid #ececec',
        borderRadius: 4,
        backgroundColor: '#ffffff',
    },
    cartaoZoomBarra: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    cartaoZoomValor: {
        minWidth: 48,
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 600,
        color: '#666',
    },
    cartaoImagem: {
        display: 'block',
        maxWidth: '100%',
        maxHeight: 260,
        margin: 'auto',
        borderRadius: 4,
        cursor: 'zoom-in',
    },
    cartaoAviso: {
        padding: theme.spacing(3, 1),
        color: '#999',
    },
    candidato: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderRadius: 4,
        border: '1px solid #e0e0e0',
    },
    resumoTroca: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: 4,
        border: '1px solid #ffe0b2',
        borderLeft: '4px solid #ef6c00',
        backgroundColor: '#fff8e1',
    },
    alerta: {
        marginTop: theme.spacing(1),
        color: '#c62828',
    },
}));

const regiaoDaProva = prova => {
    if (!prova) return '--';

    return `${prova.regiao || '--'}${prova.regiaoGrupo ? ` / ${prova.regiaoGrupo}` : ''}`;
};

const nomeDoCandidato = candidato => (candidato ? (candidato.nomeExibicao || candidato.nome || '--') : null);

const SeletorProva = ({ titulo, prova, outroSelecionadoId, desabilitado, onSelecionar, onLimpar, onErro }) => {
    const classes = useStyles();

    const [campoBusca, setCampoBusca] = useState('codigo');
    const [termo, setTermo] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscou, setBuscou] = useState(false);
    const [carregando, setCarregando] = useState(false);

    const [urlCartao, setUrlCartao] = useState(null);
    const [statusCartao, setStatusCartao] = useState('idle');
    const [zoom, setZoom] = useState(ZOOM_MIN);

    const provaId = prova ? prova.id : null;

    useEffect(() => {
        setZoom(ZOOM_MIN);

        if (!provaId) {
            setUrlCartao(null);
            setStatusCartao('idle');
            return;
        }

        let cancelado = false;
        let url = null;

        setStatusCartao('carregando');

        ProvaService.downloadCartao(provaId)
            .then(res => {
                if (cancelado) return;

                url = URL.createObjectURL(res.data);
                setUrlCartao(url);
                setStatusCartao('ok');
            })
            .catch(() => {
                if (cancelado) return;

                setUrlCartao(null);
                setStatusCartao('erro');
            });

        return () => {
            cancelado = true;

            if (url) URL.revokeObjectURL(url);
        };
    }, [provaId]);

    const buscar = useCallback(async () => {
        const valor = campoBusca === 'cpf' ? removeCpfMask(termo) : termo.trim();

        if (!valor) return;

        setCarregando(true);

        const filtros = [
            { field: campoBusca, value: valor },
            { field: 'page', value: 0 },
            { field: 'size', value: TAMANHO_PAGINA },
        ];

        try {
            const resposta = await ProvaService.filters(filtros);
            const dados = resposta.data || {};
            const conteudo = Array.isArray(dados.content) ? dados.content : [];

            setResultados(conteudo);
            setBuscou(true);
        } catch (error) {
            setResultados([]);
            setBuscou(true);
            onErro(descreverErro(error));
        } finally {
            setCarregando(false);
        }
    }, [campoBusca, termo, onErro]);

    const selecionar = p => {
        if (!p || desabilitado || p.id === outroSelecionadoId) return;

        onSelecionar(p);
    };

    const aplicarZoom = passo => setZoom(anterior =>
        Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number((anterior + passo).toFixed(2))))
    );

    const alternarZoom = () => setZoom(anterior => (anterior > ZOOM_MIN ? ZOOM_MIN : 2));

    return (
        <div className={classes.slot}>
            <Typography variant="subtitle2" className={classes.slotTitulo}>{titulo}</Typography>

            {!prova &&
                <>
                    <div className={classes.radiosInline}>
                        <Typography variant="body2" className={classes.radiosInlineLabel}>Buscar por:</Typography>
                        <RadioGroup
                            row
                            value={campoBusca}
                            onChange={event => setCampoBusca(event.target.value)}
                        >
                            {CAMPOS_BUSCA.map(opcao =>
                                <FormControlLabel
                                    key={opcao.value}
                                    value={opcao.value}
                                    control={<Radio />}
                                    label={opcao.label}
                                    disabled={carregando}
                                />
                            )}
                        </RadioGroup>
                    </div>

                    <div className={classes.buscaLinha}>
                        <div className={classes.buscaCampo}>
                            <Text
                                label={ROTULO_CAMPO[campoBusca]}
                                value={termo}
                                onChange={event => setTermo(event.target.value)}
                                onKeyDown={event => { if (event.key === 'Enter') buscar(); }}
                                variant="outlined"
                                disabled={carregando}
                            />
                        </div>
                        <ButtonPrimary
                            name="Buscar"
                            startIcon={<SearchIcon />}
                            onClick={buscar}
                            disabled={!termo.trim() || carregando}
                            style={{ marginTop: 16, height: 56 }}
                        />
                    </div>

                    <div className={classes.resultados}>
                        {carregando &&
                            <div className={classes.vazio}>
                                <Typography variant="body2" color="textSecondary">Buscando provas...</Typography>
                            </div>
                        }

                        {!carregando && !buscou &&
                            <div className={classes.vazio}>
                                <Typography variant="body2" color="textSecondary">Informe o Código da Prova ou o CPF da Prova e clique em Buscar.</Typography>
                            </div>
                        }

                        {!carregando && buscou && resultados.length === 0 &&
                            <div className={classes.vazio}>
                                <Typography variant="body2" color="textSecondary">Nenhuma prova encontrada.</Typography>
                            </div>
                        }

                        {!carregando && resultados.length > 0 &&
                            <RadioGroup value="" onChange={event => selecionar(resultados.find(p => p.id === event.target.value))}>
                                {resultados.map(p => {
                                    const impedida = p.id === outroSelecionadoId;

                                    return (
                                        <div key={p.id} className={classes.resultado}>
                                            <FormControlLabel
                                                value={p.id}
                                                disabled={impedida || desabilitado}
                                                control={<Radio color="primary" />}
                                                label={
                                                    <div className={classes.resultadoInfo}>
                                                        <div>{p.codigo}{impedida ? ' — já selecionada na outra prova' : ''}</div>
                                                        <div className={classes.resultadoSecundario}>
                                                            {p.nome || '--'} · CPF da Prova {p.cpf ? cpfMask(p.cpf) : '--'} · Região {regiaoDaProva(p)} · Candidato {nomeDoCandidato(p.candidato) || 'Sem Vínculo Registrado'}
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </RadioGroup>
                        }
                    </div>
                </>
            }

            {prova &&
                <>
                    <Typography variant="body2" color="textSecondary">
                        Código: <b>{prova.codigo}</b> — Região: {regiaoDaProva(prova)}
                    </Typography>

                    <div className={classes.cartao}>
                        {statusCartao === 'carregando' &&
                            <div className={classes.cartaoAviso}>
                                <Typography variant="body2" color="textSecondary">Carregando imagem do cartão...</Typography>
                            </div>
                        }

                        {statusCartao === 'erro' &&
                            <div className={classes.cartaoAviso}>
                                <Typography variant="body2" color="textSecondary">Imagem do cartão indisponível para validação visual.</Typography>
                            </div>
                        }

                        {statusCartao === 'ok' && urlCartao &&
                            <>
                                <div className={classes.cartaoZoomBarra}>
                                    <IconButton
                                        size="small"
                                        onClick={() => aplicarZoom(-ZOOM_PASSO)}
                                        disabled={zoom <= ZOOM_MIN}
                                        aria-label="Reduzir zoom"
                                        title="Reduzir zoom"
                                    >
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>

                                    <span className={classes.cartaoZoomValor}>{Math.round(zoom * 100)}%</span>

                                    <IconButton
                                        size="small"
                                        onClick={() => aplicarZoom(ZOOM_PASSO)}
                                        disabled={zoom >= ZOOM_MAX}
                                        aria-label="Aumentar zoom"
                                        title="Aumentar zoom"
                                    >
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        onClick={() => setZoom(ZOOM_MIN)}
                                        disabled={zoom === ZOOM_MIN}
                                        aria-label="Restaurar zoom"
                                        title="Restaurar zoom"
                                    >
                                        <RestoreIcon fontSize="small" />
                                    </IconButton>
                                </div>

                                <div className={classes.cartaoViewport}>
                                    <img
                                        src={urlCartao}
                                        alt={`Cartão da prova ${prova.codigo}`}
                                        className={classes.cartaoImagem}
                                        style={zoom > ZOOM_MIN ? { width: `${zoom * 100}%`, maxWidth: 'none', maxHeight: 'none' } : undefined}
                                        onClick={alternarZoom}
                                    />
                                </div>
                            </>
                        }
                    </div>

                    <div className={classes.candidato}>
                        <Typography variant="caption" color="textSecondary">Candidato atualmente vinculado</Typography>
                        <Typography variant="body2"><b>{nomeDoCandidato(prova.candidato) || 'Sem Vínculo Registrado'}</b></Typography>
                        {prova.candidato &&
                            <>
                                <Typography variant="body2" color="textSecondary">CPF: {prova.candidato.cpf ? cpfMask(prova.candidato.cpf) : '--'}</Typography>
                                <Typography variant="body2" color="textSecondary">Código de Prova: {prova.candidato.codigoProva || '--'}</Typography>
                            </>
                        }
                    </div>

                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                        <ButtonDefault
                            name="Trocar Prova"
                            startIcon={<ClearOutlinedIcon />}
                            onClick={onLimpar}
                            disabled={desabilitado}
                        />
                    </div>
                </>
            }
        </div>
    );
};

const DialogTrocarCandidatos = ({ open, provaInicial, onClose, onTrocado }) => {
    const classes = useStyles();

    const [provaA, setProvaA] = useState(null);
    const [provaB, setProvaB] = useState(null);
    const [trocando, setTrocando] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const notificar = useCallback((message, type = 'error') => setToast({ open: true, message, type }), []);

    useEffect(() => {
        if (!open) return;

        setProvaA(provaInicial || null);
        setProvaB(null);
        setTrocando(false);
    }, [open, provaInicial]);

    const handleClose = () => {
        if (trocando) return;

        if (onClose) onClose();
    };

    const candidatoQueVaiParaA = provaB ? provaB.candidato : null;
    const candidatoQueVaiParaB = provaA ? provaA.candidato : null;

    const mesmaProva = Boolean(provaA && provaB) && provaA.id === provaB.id;
    const podeTrocar = Boolean(provaA && provaB) && !mesmaProva;

    const confirmar = async () => {
        if (!podeTrocar || trocando) return;

        setTrocando(true);

        try {
            const payload = {
                itens: [
                    { idProva: provaA.id, idCandidato: candidatoQueVaiParaA ? candidatoQueVaiParaA.id : null },
                    { idProva: provaB.id, idCandidato: candidatoQueVaiParaB ? candidatoQueVaiParaB.id : null },
                ]
            };

            await ProvaService.trocarCandidatos(payload);

            if (onTrocado) onTrocado();
        } catch (error) {
            notificar(descreverErro(error));
        } finally {
            setTrocando(false);
        }
    };

    const acoes = (
        <>
            <ButtonPrimary
                name="Trocar Candidatos"
                startIcon={<SwapHorizOutlinedIcon />}
                onClick={confirmar}
                disabled={!podeTrocar || trocando}
                style={{ margin: 4 }}
            />
            <ButtonDefault
                name="Cancelar"
                startIcon={<ClearOutlinedIcon />}
                onClick={handleClose}
                disabled={trocando}
                style={{ margin: 4 }}
            />
        </>
    );

    return (
        <>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <SimpleDialog
                openDialog={Boolean(open)}
                title="Trocar Candidatos entre Provas"
                closeDialog={handleClose}
                actions={acoes}
                fullWidth={true}
                maxWidth="lg"
            >
                <Typography variant="body2" color="textSecondary" className={classes.contexto}>
                    Localize as <b>duas provas</b> cujos cartões foram entregues trocados. Confira as <b>assinaturas nas imagens</b> dos cartões e confirme: os candidatos atualmente vinculados serão permutados entre as duas provas.
                    <span className={classes.contextoAdendo}>
                        A troca apenas permuta os vínculos entre as provas. O <b>nome</b> e o <b>CPF</b> já preenchidos no cartão <b>não</b> são alterados.
                    </span>
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <SeletorProva
                            titulo="Prova 1"
                            prova={provaA}
                            outroSelecionadoId={provaB ? provaB.id : null}
                            desabilitado={trocando}
                            onSelecionar={setProvaA}
                            onLimpar={() => setProvaA(null)}
                            onErro={notificar}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SeletorProva
                            titulo="Prova 2"
                            prova={provaB}
                            outroSelecionadoId={provaA ? provaA.id : null}
                            desabilitado={trocando}
                            onSelecionar={setProvaB}
                            onLimpar={() => setProvaB(null)}
                            onErro={notificar}
                        />
                    </Grid>
                </Grid>

                {mesmaProva &&
                    <Typography variant="body2" className={classes.alerta}>
                        Selecione duas provas diferentes.
                    </Typography>
                }

                {podeTrocar &&
                    <div className={classes.resumoTroca}>
                        <Typography variant="subtitle2">Resultado da troca</Typography>
                        <Typography variant="body2">
                            Prova <b>{provaA.codigo}</b> passará a ter o candidato <b>{nomeDoCandidato(candidatoQueVaiParaA) || 'sem vínculo'}</b>.
                        </Typography>
                        <Typography variant="body2">
                            Prova <b>{provaB.codigo}</b> passará a ter o candidato <b>{nomeDoCandidato(candidatoQueVaiParaB) || 'sem vínculo'}</b>.
                        </Typography>
                    </div>
                }
            </SimpleDialog>
        </>
    );
};

export default DialogTrocarCandidatos;
